import { useEffect, useState } from "react";
import { BookOpen, Download, ExternalLink, FileText, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fetchGuideBlob = async (download = false) => {
  const response = await fetch(`${API}/help/user-guide${download ? "?download=true" : ""}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  if (!response.ok) {
    let detail = "The InkSight user guide is not available yet.";
    try {
      const errorData = await response.json();
      detail = errorData?.detail || detail;
    } catch {
      // Keep the default message.
    }
    throw new Error(detail);
  }
  return response;
};

const filenameFromDisposition = (headerValue, fallback) => {
  const match = /filename="?([^";]+)"?/i.exec(headerValue || "");
  return match?.[1] || fallback;
};

export const HelpPageV1 = () => {
  const [guideStatus, setGuideStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await fetch(`${API}/help/user-guide/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!response.ok) throw new Error();
        setGuideStatus(await response.json());
      } catch {
        setGuideStatus({ available: false, detail: "Unable to check guide status." });
      } finally {
        setLoading(false);
      }
    };
    loadStatus();
  }, []);

  const openGuide = async () => {
    setWorking(true);
    try {
      const response = await fetchGuideBlob(false);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.message || "Unable to open the user guide");
    } finally {
      setWorking(false);
    }
  };

  const downloadGuide = async () => {
    setWorking(true);
    try {
      const response = await fetchGuideBlob(true);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filenameFromDisposition(response.headers.get("Content-Disposition"), "InkSight_User_Guide.pdf");
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("InkSight user guide downloaded");
    } catch (error) {
      toast.error(error.message || "Unable to download the user guide");
    } finally {
      setWorking(false);
    }
  };

  const available = guideStatus?.available === true;

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <LifeBuoy size={30} /> Help
        </h1>
        <p className="text-muted-foreground mt-1">
          Training and reference material for using InkSight.
        </p>
      </div>

      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-primary/10 p-3 text-primary">
              <BookOpen size={28} />
            </div>
            <div>
              <h2 className="text-xl font-semibold">InkSight User Guide</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Step-by-step instructions for Sales Ops and sales users, including creating requests,
                checking project status, using the activity timeline, and understanding files.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-1">
                  <FileText size={13} /> Current internal guide
                </span>
                {loading ? (
                  <span>Checking availability...</span>
                ) : available ? (
                  <span>{guideStatus.filename || "Guide is available"}</span>
                ) : (
                  <span className="text-amber-300">Guide file has not been published to the server yet.</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openGuide}
              disabled={!available || working}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground disabled:opacity-50"
            >
              <ExternalLink size={17} /> Open Guide
            </button>
            <button
              type="button"
              onClick={downloadGuide}
              disabled={!available || working}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2.5 font-medium hover:bg-secondary/80 disabled:opacity-50"
            >
              <Download size={17} /> Download
            </button>
          </div>
        </div>

        {!loading && !available && (
          <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
            {guideStatus?.detail || "The guide file is missing."}
          </div>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold">Start here</h3>
          <p className="mt-2 text-sm text-muted-foreground">Use the guide for basic navigation, project creation, and how to follow a project.</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold">For Sales Ops</h3>
          <p className="mt-2 text-sm text-muted-foreground">Reference the intake, project detail, files, and activity timeline sections.</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold">For support</h3>
          <p className="mt-2 text-sm text-muted-foreground">Use the troubleshooting section before opening an IT ticket.</p>
        </div>
      </section>
    </div>
  );
};
